export const DEFAULT_HERO_EYEBROW = "Claro Advisors";
export const LEAD_FORM_ANCHOR_ID = "lead-form";

export interface CmsAssetRenderProps {
  assetId: string;
  src: string;
  alt: string;
  caption?: string;
  contentType?: string;
}

export interface HeroModuleProps {
  eyebrow?: string;
  headline: string;
  body?: string;
  image?: CmsAssetRenderProps;
}

export interface CtaModuleProps {
  label: string;
  url: string;
}

export interface MandatoryDisclosureProps {
  version: string;
  body: string;
}

export interface AdvisorProfileModuleProps {
  name?: string;
  title?: string;
  credentials?: string[];
  bio?: string;
  phone?: string;
  email?: string;
  calendarUrl?: string;
  linkedInUrl?: string;
  location?: string;
  image?: CmsAssetRenderProps;
}

export interface ContactSectionModuleProps {
  heading?: string;
  body?: string;
  phone?: string;
  email?: string;
  calendarUrl?: string;
  addressLines?: string[];
  ctaLabel?: string;
}

export type LeadFormLayout = "SPLIT" | "CENTERED";

export interface LeadFormModuleProps {
  eyebrow?: string;
  heading?: string;
  body?: string;
  layout?: LeadFormLayout;
  submitLabel: string;
  successMessage?: string;
}

export interface GridItemProps {
  title?: string;
  body?: string;
  ctaLabel?: string;
  ctaUrl?: string;
}

export type InfoCardGridLayout =
  | "GRID"
  | "COLUMNS"
  | "RULE_LIST"
  | "LEFT"
  | "CENTERED";
export type InfoCardGridTone = "LIGHT" | "COCOA" | "SEAFOAM";

export interface InfoCardGridModuleProps {
  eyebrow?: string;
  heading?: string;
  body?: string;
  layout?: InfoCardGridLayout;
  tone?: InfoCardGridTone;
  items: GridItemProps[];
}

export type MediaTextSectionLayout = "IMAGE_LEFT" | "IMAGE_RIGHT";

export interface MediaTextSectionModuleProps {
  eyebrow?: string;
  heading?: string;
  body?: string;
  layout?: MediaTextSectionLayout;
  image?: CmsAssetRenderProps;
  ctaLabel?: string;
  ctaUrl?: string;
}

export interface ProcessStepProps {
  title?: string;
  body?: string;
}

export interface ProcessStepsModuleProps {
  eyebrow?: string;
  heading?: string;
  body?: string;
  steps: ProcessStepProps[];
}

export type QuoteSectionLayout = "LEFT" | "CENTERED";

export interface QuoteSectionModuleProps {
  eyebrow?: string;
  quote: string;
  attributionName?: string;
  attributionTitle?: string;
  layout?: QuoteSectionLayout;
  image?: CmsAssetRenderProps;
  ctaLabel?: string;
  ctaUrl?: string;
}

export interface ResourceListModuleProps {
  eyebrow?: string;
  heading?: string;
  body?: string;
  items: {
    title?: string;
    description?: string;
    linkLabel: string;
    url: string;
  }[];
}

export interface ImportantDisclosuresModuleProps {
  heading?: string;
  paragraphs: {
    body?: string;
  }[];
  footnotes: ImportantDisclosureFootnoteProps[];
}

export interface ImportantDisclosureFootnoteProps {
  key?: string;
  label?: string;
  body?: string;
}

export interface FaqModuleProps {
  eyebrow?: string;
  heading?: string;
  items: {
    question: string;
    answer: string;
  }[];
}

export type LongFormContentType = "NEWS_ARTICLE";
export type LongFormTemplateKey = "STANDARD_ARTICLE";
export type LongFormBlockType =
  | "HEADING"
  | "PARAGRAPH"
  | "QUOTE"
  | "IMAGE"
  | "CTA";

export interface LongFormContentBlockRenderProps {
  blockType: LongFormBlockType;
  text?: string;
  level?: number;
  attribution?: string;
  image?: CmsAssetRenderProps;
  caption?: string;
  label?: string;
  url?: string;
}

export interface LongFormArticleRenderProps {
  articleId: string;
  contentType: LongFormContentType;
  templateKey: LongFormTemplateKey;
  title: string;
  slug: string;
  excerpt?: string;
  body: LongFormContentBlockRenderProps[];
  authorDisplayName?: string;
  displayDate?: string;
  heroImage?: CmsAssetRenderProps;
  seoTitle?: string;
  metaDescription?: string;
}

export type CmsModuleType =
  | "HERO"
  | "CTA"
  | "ADVISOR_PROFILE"
  | "CONTACT_SECTION"
  | "LEAD_FORM"
  | "INFO_CARD_GRID"
  | "MEDIA_TEXT_SECTION"
  | "PROCESS_STEPS"
  | "QUOTE_SECTION"
  | "RESOURCE_LIST"
  | "IMPORTANT_DISCLOSURES"
  | "FAQ";

export interface CmsModuleRenderProps {
  moduleId?: string;
  moduleType: CmsModuleType;
  hero?: HeroModuleProps;
  cta?: CtaModuleProps;
  advisorProfile?: AdvisorProfileModuleProps;
  contactSection?: ContactSectionModuleProps;
  leadForm?: LeadFormModuleProps;
  infoCardGrid?: InfoCardGridModuleProps;
  mediaTextSection?: MediaTextSectionModuleProps;
  processSteps?: ProcessStepsModuleProps;
  quoteSection?: QuoteSectionModuleProps;
  resourceList?: ResourceListModuleProps;
  importantDisclosures?: ImportantDisclosuresModuleProps;
  faq?: FaqModuleProps;
}

export interface CmsThemeTokens {
  colorBackground: string;
  colorSurface: string;
  colorText: string;
  colorMutedText: string;
  colorAccent: string;
  colorAccentText: string;
  colorBorder: string;
  fontFamily: string;
  maxWidth: string;
}

export interface CmsPageRenderProps {
  slug: string;
  siteName: string;
  leadFormToken?: string;
  pageTitle: string;
  metaDescription?: string;
  advisorDisplayName: string;
  mandatoryDisclosure: MandatoryDisclosureProps;
  modules: CmsModuleRenderProps[];
  longFormContent: LongFormArticleRenderProps[];
  theme: CmsThemeTokens;
}
